/** Uploads buffered chunks (decision 0051): for each chunk, ask for one pre-signed PUT with the recording's bearer
 * token (no console cookie, so it outlives the console sign-in), decrypt the buffered copy, PUT it, and only then drop
 * the copy from the device. Sequential per track; retries with backoff; never gives up on a chunk. */
import { aadFor, decryptChunk } from "./crypto.js";
import type { ChunkMeta, ChunkStore } from "./store.js";
import { ApiFailure } from "./types.js";
import type { RecorderApi, SignedPut, Track, Unsent, UnsentCause } from "./types.js";

export interface QueuedChunk {
  meta: ChunkMeta;
  enqueuedAt: number; // ms, the engine's clock
  plain?: Uint8Array<ArrayBuffer>; // set only when the device refused the write: the chunk is held in memory
  attempts: number;
  signed?: SignedPut; // MEMORY ONLY (never written to the device store: it is a 5 minute write capability). The pre-signed PUT in hand: reused while it is valid, so a failing PUT does not use up the chunk's URL requests
}

export interface UploaderHooks {
  now(): number;
  sleep(ms: number): Promise<void>;
  onSignedOut(): void; // a 401: uploads pause until setToken()
  onRefused(code: string): void;
  onStored(meta: ChunkMeta): void;
}

// The pause between tries grows to five minutes and stays there: a chunk is never given up on, and stays retryable for the whole
// 72 hour window, so a network that is down for hours costs a few dozen quiet tries, not the chunk.
const BACKOFF_MS = [1000, 2000, 5000, 10000, 30000, 60000, 120000, 300000];
// A URL is reused only while this much of its life is left; the server limits a chunk to 60 URL requests per credential (the count starts again at each resume).
const URL_SAFETY_MS = 30_000;

export class Uploader {
  private queues: Record<Track, QueuedChunk[]> = { mic: [], call: [] };
  private running: Record<Track, Promise<void> | null> = { mic: null, call: null };
  private paused = false;
  private stopped = false;
  lastCause: UnsentCause = null;
  lastRefusal: string | null = null;
  uploaded = 0;

  constructor(
    private readonly api: RecorderApi,
    private readonly store: ChunkStore,
    private key: CryptoKey,
    private token: string,
    private readonly recordingId: string,
    private readonly hooks: UploaderHooks,
  ) {}

  setToken(token: string, key?: CryptoKey): void {
    this.token = token;
    if (key) this.key = key;
    this.paused = false;
    this.lastCause = null;
    for (const t of ["mic", "call"] as Track[]) void this.pump(t);
  }

  enqueue(chunk: QueuedChunk): void {
    this.queues[chunk.meta.track].push(chunk);
    void this.pump(chunk.meta.track);
  }

  pending(): number {
    return this.queues.mic.length + this.queues.call.length;
  }

  /** What is waiting, for the "X minutes not yet uploaded" notice. `minutes` is the longer track's unsent audio. */
  summary(expiresAt: string, now: number): Unsent | null {
    const mine = [...this.queues.mic, ...this.queues.call];
    if (mine.length === 0) return null;
    const secs = (q: QueuedChunk[]) => q.reduce((n, c) => n + c.meta.duration_ms, 0) / 1000;
    const oldest = Math.min(...mine.map((c) => c.enqueuedAt));
    return {
      minutes: Math.round((Math.max(secs(this.queues.mic), secs(this.queues.call)) / 60) * 10) / 10,
      chunks: mine.length,
      oldest_age_s: Math.max(0, Math.round((now - oldest) / 1000)),
      expires_at: expiresAt,
      warn_24h: Date.parse(expiresAt) - now <= 24 * 3600 * 1000,
    };
  }

  /** Resolves when both queues are empty (or the uploader was stopped). */
  async drained(): Promise<void> {
    while (!this.stopped && this.pending() > 0) {
      await Promise.all([this.running.mic, this.running.call].filter((p): p is Promise<void> => p !== null));
      if (this.pending() > 0 && this.running.mic === null && this.running.call === null) {
        if (this.paused) await this.hooks.sleep(250);
        else for (const t of ["mic", "call"] as Track[]) void this.pump(t);
      }
    }
  }

  stop(): void {
    this.stopped = true;
  }

  private pump(track: Track): Promise<void> {
    if (this.running[track]) return this.running[track]!;
    this.running[track] = this.loop(track).finally(() => {
      this.running[track] = null;
      if (this.queues[track].length > 0 && !this.paused && !this.stopped) void this.pump(track);
    });
    return this.running[track]!;
  }

  private async loop(track: Track): Promise<void> {
    const q = this.queues[track];
    while (q.length > 0 && !this.paused && !this.stopped) {
      const item = q[0]!;
      let reachedPut = false;
      try {
        const plain = item.plain ?? (await this.read(item.meta));
        const usable = item.signed && Date.parse(item.signed.expires_at) - this.hooks.now() > URL_SAFETY_MS;
        const signed =
          usable && item.signed
            ? item.signed
            : (item.signed = await this.api.chunkUrl(this.token, this.recordingId, {
                track,
                seq: item.meta.seq,
                bytes: plain.length,
                started_ms: item.meta.started_ms,
                duration_ms: item.meta.duration_ms,
                content_type: item.meta.content_type,
              }));
        reachedPut = true;
        const status = await this.api.put(signed.url, signed.headers, plain);
        // 412: the key is already written (an earlier PUT's answer was lost). Write-once, so it is stored.
        if (status !== 200 && status !== 412) throw new ApiFailure(status, "put_failed");
        q.shift();
        this.uploaded += 1;
        this.lastCause = null;
        this.lastRefusal = null;
        try {
          await this.store.deleteChunk(this.recordingId, track, item.meta.seq);
        } catch {
          /* a leftover copy is deleted with the recording */
        }
        this.hooks.onStored(item.meta);
      } catch (e) {
        item.attempts += 1;
        const status = e instanceof ApiFailure ? e.status : 0;
        // A 4xx from storage (an expired or rejected signature) means this URL is no good; a network failure or 5xx does not.
        if (reachedPut && e instanceof ApiFailure && e.code === "put_failed" && status >= 400 && status < 500) item.signed = undefined;
        if (status === 401) {
          this.paused = true;
          this.lastCause = "signed_out";
          this.hooks.onSignedOut();
          return;
        }
        if (status === 409 && e instanceof ApiFailure && e.code === "beyond_manifest") {
          q.shift(); // the server never wanted this chunk (it is past the declared recording)
          continue;
        }
        this.lastCause = status >= 400 && status < 500 ? "refused" : typeof navigator !== "undefined" && navigator.onLine === false ? "offline" : "slow";
        if (this.lastCause === "refused") {
          this.lastRefusal = e instanceof ApiFailure ? e.code : "refused";
          this.hooks.onRefused(this.lastRefusal);
        }
        await this.hooks.sleep(BACKOFF_MS[Math.min(item.attempts - 1, BACKOFF_MS.length - 1)]!);
      }
    }
  }

  private async read(meta: ChunkMeta): Promise<Uint8Array<ArrayBuffer>> {
    const data = await this.store.getChunk(this.recordingId, meta.track, meta.seq);
    if (!data) throw new ApiFailure(0, "chunk_missing_on_device");
    return decryptChunk(this.key, aadFor(this.recordingId, meta.track, meta.seq), data);
  }
}
